import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './AgriculturalEquipment.css';
import { useProducts, type AgriculturalSpecs } from '../../api/products/products-api';
import { useAuth } from '../../api/auth/AuthContext';
import DbSection from '../../components/DbSection';
import ProductCard, { ProductCardSkeleton } from '../../components/ProductCard';

const AgriculturalEquipment: React.FC = () => {
  const { authType } = useAuth();
  const location = useLocation();
  const [editMode, setEditMode] = useState<boolean>(!!(location.state as any)?.editMode);
  const { products, loading, error } = useProducts<AgriculturalSpecs>('agricultural', editMode);
  const navigate = useNavigate();
  const weighingEquipment = products.filter(p => !p.specs?.type?.startsWith('Indicador'));
  const weightIndicators = products.filter(p => p.specs?.type?.startsWith('Indicador'));

  const benefits = [
    {
      title: "Control de Inventario",
      description: "Monitoreo preciso de inventarios de grano, alimento y productos agrícolas.",
    },
    {
      title: "Optimización de Alimentación",
      description: "Dosificación exacta de alimentos para maximizar el rendimiento ganadero.",
    },
    {
      title: "Gestión de Ganado",
      description: "Pesaje preciso para control de crecimiento y salud animal.",
    },
    {
      title: "Eficiencia Operativa",
      description: "Reducción de desperdicios y mejora en la gestión de recursos.",
    },
    {
      title: "Trazabilidad",
      description: "Registro detallado de datos para cumplimiento normativo.",
    },
    {
      title: "Rentabilidad",
      description: "Mejora en la rentabilidad a través del control preciso de insumos.",
    },
  ];

  const sectors = [
    {
      title: "Ganadería",
      description: "Pesaje de ganado bovino, porcino y ovino para control de crecimiento y comercialización.",
    },
    {
      title: "Agricultura",
      description: "Pesaje de granos, semillas y productos agrícolas en silos y almacenes.",
    },
    {
      title: "Maquinaria Agrícola",
      description: "Integración en tractores, cosechadoras y equipos de manejo de materiales.",
    },
    {
      title: "Procesamiento",
      description: "Control de peso en plantas procesadoras de alimentos y productos agrícolas.",
    },
    {
      title: "Transporte",
      description: "Verificación de carga en camiones y equipos de transporte agrícola.",
    },
    {
      title: "Investigación",
      description: "Estudios de rendimiento y desarrollo en centros de investigación agrícola.",
    },
  ];

  const overviewFeatures = [
    "Básculas especializadas para pesaje de animales",
    "Indicadores para dosificación de ingredientes",
    "Celdas de carga para aplicaciones agrícolas",
    "Sistemas portátiles y permanentes",
    "Tecnología que reduce errores por movimiento",
    "Soluciones económicas, precisas y confiables",
  ];

  return (
    <div className="ag-page">
      {error && <div className="ag-status-bar ag-status-bar--error"><p>Error al cargar productos. Por favor intente de nuevo.</p></div>}

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="ag-hero">
        <div className="container ag-hero-content">
          <nav className="ag-breadcrumb" aria-label="Navegación de ruta">
            <Link to="/">Inicio</Link>
            <span className="ag-breadcrumb-sep" aria-hidden="true">/</span>
            <Link to="/productos">Productos</Link>
            <span className="ag-breadcrumb-sep" aria-hidden="true">/</span>
            <span>Equipos Agropecuarios</span>
          </nav>
          <h1 className="ag-hero-title">Equipos Agropecuarios</h1>
          <p className="ag-hero-subtitle">
            Amplia gama de básculas y equipos agrícolas para resolver diversas
            necesidades en granjas y ranchos.
          </p>
          {authType === 'internal' && (
            <button
              className={`ag-btn-edit-mode${editMode ? ' ag-btn-edit-mode--active' : ''}`}
              onClick={() => setEditMode(m => !m)}
            >
              {editMode ? 'Salir de edición' : 'Modo edición'}
            </button>
          )}
        </div>
        <div className="ag-hero-image" aria-hidden="true">
          <img src="/images/agricultural-equipment.jpg" alt="" loading="lazy" />
        </div>
      </section>

      {/* ── Overview ─────────────────────────────────────────── */}
      <section className="ag-overview">
        <div className="container">
          <div className="ag-overview-grid">
            <div className="ag-overview-text">
              <span className="ag-label">Descripción general</span>
              <h2>Soluciones para el campo</h2>
              <p>
                Proporcionamos una amplia gama de básculas y equipos agrícolas para resolver diversas
                necesidades en granjas, incluyendo básculas para pesaje de animales, indicadores de peso
                para dosificación de ingredientes, y celdas de carga para básculas de plataforma,
                básculas de camión, silos y más.
              </p>
            </div>
            <div className="ag-overview-image">
              <ul className="ag-features-list" aria-label="Características principales">
              {overviewFeatures.map((feature, index) => (
                <li key={index} className="ag-feature-item">
                  <svg className="ag-check-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M7 10l2.5 2.5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>
            </div>
          </div>
        </div>
      </section>


      {/* ── Weighing Equipment ───────────────────────────────── */}
      <section className="ag-products">
        <div className="container">
          <DbSection>
            <div className="catalog-header">
              <h2 className="catalog-title">Equipos de Pesaje</h2>
              {authType === 'internal' && editMode && (
                <button className="btn-add-product" onClick={() => navigate('/productos/nuevo/agricultural')}>
                  + Agregar producto
                </button>
              )}
            </div>
            <div className="models-grid">
              {loading
                ? Array.from({ length: 3 }, (_, i) => <ProductCardSkeleton key={i} />)
                : weighingEquipment.map((equipment, index) => (
                <ProductCard
                  key={index}
                  reviewed={equipment.reviewed}
                  endOfLife={equipment.endOfLife}
                  productId={equipment.productId}
                  name={equipment.name}
                  badge={equipment.specs?.type ?? undefined}
                  description={equipment.specs?.description ?? undefined}
                  image={equipment.image}
                  onRevisar={editMode && equipment.productId ? (id) => navigate(`/productos/revisar/${encodeURIComponent(id)}`, { state: { editMode: true, from: location.pathname } }) : undefined}
                  editMode={editMode}
                  sections={[
                    ...(equipment.specs?.capacities?.length ? [{ icon: '📦', label: 'Capacidades', items: (equipment.specs?.capacities ?? []).filter((x): x is string => x != null) }] : []),
                    ...(equipment.specs?.features?.length ? [{ icon: '⚙️', label: 'Características', items: (equipment.specs?.features ?? []).filter((x): x is string => x != null) }] : []),
                    ...(equipment.specs?.applications?.length ? [{ icon: '🏭', label: 'Aplicaciones', items: (equipment.specs?.applications ?? []).filter((x): x is string => x != null) }] : []),
                  ]}
                />
              ))}
            </div>
          </DbSection>
        </div>
      </section>

      {/* ── Weight Indicators ────────────────────────────────── */}
      <section className="ag-indicators">
        <div className="container">
          <DbSection>
            <div className="catalog-header">
              <h2 className="catalog-title">Indicadores de Peso</h2>
            </div>
            <div className="models-grid">
              {weightIndicators.map((indicator, index) => (
                <ProductCard
                  key={index}
                  reviewed={indicator.reviewed}
                  endOfLife={indicator.endOfLife}
                  productId={indicator.productId}
                  name={indicator.name}
                  badge={indicator.specs?.type ?? undefined}
                  description={indicator.specs?.description ?? undefined}
                  onRevisar={editMode && indicator.productId ? (id) => navigate(`/productos/revisar/${encodeURIComponent(id)}`, { state: { editMode: true, from: location.pathname } }) : undefined}
                  editMode={editMode}
                  sections={[
                    ...(indicator.specs?.features?.length ? [{ icon: '⚙️', label: 'Características', items: (indicator.specs?.features ?? []).filter((x): x is string => x != null) }] : []),
                    ...(indicator.specs?.applications?.length ? [{ icon: '🏭', label: 'Aplicaciones', items: (indicator.specs?.applications ?? []).filter((x): x is string => x != null) }] : []),
                  ]}
                />
              ))}
            </div>
          </DbSection>
        </div>
      </section>

      {/* ── Benefits ─────────────────────────────────────────── */}
      <section className="ag-benefits">
        <div className="container">
          <span className="ag-label ag-label--light">Ventajas</span>
          <h2 className="ag-section-title ag-section-title--light">Beneficios para el Sector Agrícola</h2>
          <div className="ag-benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="ag-benefit-card">
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Sectors ──────────────────────────────────────────── */}
      <section className="ag-sectors">
        <div className="container">
          <span className="ag-label">Industrias</span>
          <h2 className="ag-section-title">Sectores de Aplicación</h2>
          <div className="ag-sectors-grid">
            {sectors.map((sector, index) => (
              <div key={index} className="ag-sector-card">
                <h3>{sector.title}</h3>
                <p>{sector.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AgriculturalEquipment;
